import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Accordion,
  Col,
  IconButton,
  KeyValue,
  Label,
  Row,
} from '@folio/stripes-components';
import { IfPermission } from '@folio/stripes-core';
import HelpeModal from '../../../../../shared/HelpeModal';

function EntityData({ entityData, basePath }) {
  const intl = useIntl();

  const renderAccordionHeader = (fieldLabel, defaultField) => {
    return (
      <>
        <IfPermission perm="ui-plugin-inventory-templates.defaultFields.edit">
          <IconButton
            icon="edit"
            to={`${basePath}/defaultFields/${defaultField?.id}/edit`}
            data-test-custom-field-delete-button
          />
        </IfPermission>
        <HelpeModal
          fieldData={defaultField}
          title={fieldLabel}
          iconSize="medium"
        />
      </>
    );
  };

  return (
    <div style={{ paddingLeft: '40px', paddingRight: '40px' }}>
      {entityData.map((defaultField, index) => {
        const localizedName = defaultField.label?.find(
          (field) => field?.locale === intl.locale,
        )?.value;

        const localizedSingularName = defaultField.singularLabel?.find(
          (field) => field?.locale === intl.locale,
        )?.value;

        return (
          <Accordion
            id={`${defaultField.name}-entity-data-${index}`}
            key={defaultField.id}
            label={<Label>{localizedName}</Label>}
            displayWhenOpen={renderAccordionHeader(localizedName, defaultField)}
            displayWhenClosed={renderAccordionHeader(
              localizedName,
              defaultField,
            )}
            closedByDefault
            separator={index !== 0}
          >
            <Row>
              {defaultField.isRepeatable && (
                <Col smOffset={0} sm={4}>
                  <KeyValue
                    label={
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.defaultFields.view.singularLabel"
                        defaultMessage="Singular label"
                      />
                    }
                    value={localizedSingularName}
                  />
                </Col>
              )}
              <Col smOffset={0} sm={4}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.defaultFields.view.type"
                      defaultMessage="Field type"
                    />
                  }
                  value={defaultField.type}
                />
              </Col>
              <Col smOffset={0} sm={4}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.defaultFields.view.isRepeatable"
                      defaultMessage="Is repeatable"
                    />
                  }
                  value={defaultField.isRepeatable.toString()}
                />
              </Col>
              <Col smOffset={0} sm={4}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.defaultFields.view.entityType"
                      defaultMessage="Record type"
                    />
                  }
                  value={defaultField.entityType}
                />
              </Col>
            </Row>
            <Row>
              <Col smOffset={0} sm={3}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.customFields.view.fieldName"
                      defaultMessage="Field name"
                    />
                  }
                  value={defaultField.name}
                />
              </Col>
              <Col smOffset={0} sm={3}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.defaultFields.view.isRequired"
                      defaultMessage="Is required"
                    />
                  }
                  value={defaultField.isRequired.toString()}
                />
              </Col>
              <Col smOffset={0} sm={3}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.defaultFields.view.canEdit"
                      defaultMessage="Can edit"
                    />
                  }
                  value={defaultField.canEdit.toString()}
                />
              </Col>
              <Col smOffset={0} sm={3}>
                <KeyValue
                  label={
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.defaultFields.view.defaultValue"
                      defaultMessage="Default value"
                    />
                  }
                  value={defaultField.defaultValue}
                />
              </Col>
            </Row>
          </Accordion>
        );
      })}
    </div>
  );
}

EntityData.propTypes = {
  entityData: PropTypes.arrayOf(PropTypes.object),
  basePath: PropTypes.string,
};

export default EntityData;
